/**
 * The case workspace (S-A41) — the screen this product is used from.
 *
 * The whole detail payload arrives in one round trip, including the team, the open
 * tasks, the critical dates, the outstanding documents, the financial position and
 * the available transitions. Do not fan out into separate requests for those: the
 * server composes them under one authorization pass, and refetching them
 * independently would give panels that disagree with each other.
 *
 * Two things the API decides, not this screen:
 *   - which panels EXIST (a `null` block means no capability — see CaseOverviewTab)
 *   - which actions are OFFERED (`_actions` and `available_transitions`)
 *
 * And the standing rule: both are for rendering. The server re-checks on execution.
 */

import {
  qk,
  useApi,
  useApiMutation,
  useApiQuery,
  usePaginatedList,
  type CaseDetail,
  type DocumentListItem,
} from '@legalcrm/api';
import {
  Badge,
  Button,
  ErrorState,
  Skeleton,
  StatusPill,
  TabPanel,
  Tabs,
  statusTone,
  useFieldId,
} from '@legalcrm/ui';
import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { STAFF } from '@legalcrm/api';
import { Can } from '@/auth/permissions';
import { useAuth } from '@/auth/AuthProvider';
import { formatDate, humanise } from '@/lib/format';
import { DeleteWithReasonDialog } from '@/components/DeleteWithReasonDialog';
import { PageHeader } from '@/shell/PageHeader';
import { ReviewPanel } from '@/routes/documents/DocumentReviewPage';
import { DocumentsTable } from '@/routes/documents/DocumentsTable';
import { RequestDocumentsPanel } from '@/routes/documents/RequestDocumentsPanel';

import { CaseNotesTab } from './CaseNotesTab';
import { EditCaseModal } from './EditCaseModal';
import { CaseOverviewTab } from './CaseOverviewTab';
import { CaseTimelineTab } from './CaseTimelineTab';
import { ClientVisibilityNotice } from './ClientVisibilityNotice';
import { StageSuggestionBanner } from './StagePath';
import { TransitionDialog } from './TransitionDialog';

type TabKey = 'overview' | 'documents' | 'timeline' | 'notes';

export function CaseWorkspacePage() {
  const { caseId = '' } = useParams();
  const api = useApi();
  const navigate = useNavigate();
  const { matterLabel } = useAuth();
  const baseId = useFieldId();
  const [tab, setTab] = useState<TabKey>('overview');
  const [transitionOpen, setTransitionOpen] = useState(false);
  const [editing, setEditing] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const query = useApiQuery<CaseDetail>(qk.detail('cases', caseId), STAFF.case(caseId), {
    enabled: Boolean(caseId),
  });

  // Above the early returns. Hook order must not change between renders.
  const remove = useApiMutation<unknown, string>(
    {
      mutationFn: (reason) => api.delete(STAFF.case(caseId), { body: { reason } }),
      invalidates: [qk.all('cases'), qk.dashboard()],
    },
    { onSuccess: () => navigate('/cases') },
  );

  if (query.isLoading) return <WorkspaceSkeleton />;

  // A 404 here means "absent as far as you are concerned" — it covers a matter that
  // does not exist, one belonging to another firm, and one outside this reader's
  // scope, deliberately indistinguishably. `ErrorState` knows not to say anything
  // about permissions for a 404.
  if (query.error || !query.data) {
    return <ErrorState error={query.error} onRetry={() => void query.refetch()} />;
  }

  const caseRecord = query.data;

  const tabs = [
    { key: 'overview', label: 'Overview' },
    {
      key: 'documents',
      label: 'Documents',
      badge: caseRecord.outstanding_documents?.length || undefined,
    },
    { key: 'timeline', label: 'Timeline' },
    { key: 'notes', label: 'Notes & updates' },
  ];

  return (
    <>
      <PageHeader
        title={caseRecord.title}
        description={`${caseRecord.client_name} · ${caseRecord.service_name}`}
        meta={
          <>
            <span className="font-mono text-sm text-ink-muted">{caseRecord.case_number}</span>
            <StatusPill
              label={humanise(caseRecord.status)}
              tone={statusTone(caseRecord.status)}
            />
            {/* The firm's configured stage NAME. The key is internal vocabulary. */}
            <Badge tone="info">{caseRecord.current_stage.name}</Badge>
            {caseRecord.priority !== 'normal' && (
              <Badge tone={caseRecord.priority === 'urgent' ? 'danger' : 'warning'} size="sm">
                {humanise(caseRecord.priority)} priority
              </Badge>
            )}
            {caseRecord.target_completion_at && (
              <span className="text-sm text-ink-muted">
                Target {formatDate(caseRecord.target_completion_at)}
              </span>
            )}
          </>
        }
        actions={
          <>
            {/* `_actions` is the accurate check: it is the kernel's answer for THIS
                record, scope included, rather than a capability check. */}
            <Can actions={caseRecord._actions} action="transition">
              <Button variant="primary" onClick={() => setTransitionOpen(true)}>
                Change stage
              </Button>
            </Can>
            <Can actions={caseRecord._actions} action="edit">
              <Button variant="secondary" onClick={() => setEditing(true)}>
                Edit
              </Button>
            </Can>
            {/* Capability rather than `_actions`: deletion is an administrator and
                partner grant, and the server refuses a matter that is still live
                with a reason this dialog surfaces. */}
            <Can module="case" action="delete">
              <Button variant="ghost" onClick={() => setDeleting(true)}>
                Delete
              </Button>
            </Can>
          </>
        }
      />

      {/* The server's answer, not a guess from the client record: it knows which contacts
          hold this matter and which of them can actually sign in. */}
      {!caseRecord.client_can_see && (
        <ClientVisibilityNotice clientId={caseRecord.client} matterLabel={matterLabel} />
      )}

      <DeleteWithReasonDialog
        open={deleting}
        onClose={() => setDeleting(false)}
        label={caseRecord.case_number}
        noun="matter"
        caveat="A matter that is still live must be closed or cancelled through the workflow first — that records why the work stopped."
        busy={remove.isPending}
        error={remove.error}
        onConfirm={(reason) => remove.mutateAsync(reason)}
      />

      <EditCaseModal
        open={editing}
        onClose={() => setEditing(false)}
        caseRecord={caseRecord}
      />

      <StageSuggestionBanner caseRecord={caseRecord} />

      <Tabs
        tabs={tabs}
        value={tab}
        onChange={(key) => setTab(key as TabKey)}
        label={`${matterLabel} sections`}
        className="mb-4"
      />

      {tab === 'overview' && (
        <TabPanel baseId={baseId} tabKey="overview">
          <CaseOverviewTab caseRecord={caseRecord} />
        </TabPanel>
      )}

      {tab === 'documents' && (
        <TabPanel baseId={baseId} tabKey="documents">
          <CaseDocumentsTab caseId={caseId} clientId={caseRecord.client} />
        </TabPanel>
      )}

      {tab === 'timeline' && (
        <TabPanel baseId={baseId} tabKey="timeline">
          <CaseTimelineTab caseId={caseId} />
        </TabPanel>
      )}

      {tab === 'notes' && (
        <TabPanel baseId={baseId} tabKey="notes">
          <CaseNotesTab caseId={caseId} />
        </TabPanel>
      )}

      <TransitionDialog
        open={transitionOpen}
        onClose={() => setTransitionOpen(false)}
        caseRecord={caseRecord}
        transitions={caseRecord.available_transitions}
      />
    </>
  );
}

/**
 * Documents on a matter.
 *
 * Filtered by CLIENT, not by case: `DocumentViewSet` scopes by the document's case
 * being assigned to the actor OR the document being client-level for a client in
 * scope. Documents legitimately exist before a case does — an identity document
 * collected at onboarding belongs to the client, not the matter (CR-03).
 */
function CaseDocumentsTab({ caseId, clientId }: { caseId: string; clientId: string }) {
  // The same drawer the library and the review queue open, so a document on a matter
  // can be opened, reviewed or deleted from where somebody working the matter finds it.
  const [openId, setOpenId] = useState<string | null>(null);
  const list = usePaginatedList<DocumentListItem>(
    qk.sub('cases', caseId, 'documents'),
    STAFF.documents,
    { client: clientId },
  );

  return (
    <div className="flex flex-col gap-4">
      {/* What the firm has asked for comes first. The client's portal is driven by
          this list, and a matter with an empty document table usually means nobody
          has asked yet rather than that the client has not replied. */}
      <RequestDocumentsPanel clientId={clientId} caseId={caseId} />

      {openId && <ReviewPanel documentId={openId} onClose={() => setOpenId(null)} />}

      <DocumentsTable
        rows={list.items}
        loading={list.isLoading}
        error={list.error}
        onRetry={() => void list.refetch()}
        onRowClick={(row) => setOpenId(row.id)}
        emptyTitle="Nothing received yet"
        emptyDescription="Documents the client sends in will appear here."
        showClient={false}
        pagination={{
          hasNext: list.hasNext,
          hasPrevious: list.hasPrevious,
          onNext: list.goNext,
          onPrevious: list.goPrevious,
        }}
      />
    </div>
  );
}

function WorkspaceSkeleton() {
  return (
    <div className="flex flex-col gap-4" aria-busy="true">
      <span className="sr-only">Loading matter</span>
      <Skeleton className="h-8 w-1/2" />
      <Skeleton className="h-4 w-1/3" />
      <Skeleton className="h-9 w-full" />
      <div className="grid gap-4 lg:grid-cols-3">
        <Skeleton className="h-64 lg:col-span-2" />
        <Skeleton className="h-64" />
      </div>
    </div>
  );
}
